import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        brand: "bg-brand-teal text-white hover:bg-brand-teal/90",
        brand2: "bg-brand-yellow text-[#061d4a] hover:bg-brand-yellow/90",
        success: "bg-status-complete text-white hover:bg-status-complete/90",
        info: "bg-blue-600 text-white hover:bg-blue-600/90",
        warning: "bg-brand-amber text-white hover:bg-brand-amber/90",
      },
      appearance: {
        solid: "",
        outlined: "",
        text: "bg-transparent",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        appearance: "outlined",
        class: "border border-primary text-primary bg-transparent hover:bg-primary/10",
      },
      {
        variant: "destructive",
        appearance: "outlined",
        class: "border border-destructive text-destructive bg-transparent hover:bg-destructive/10",
      },
      {
        variant: "secondary",
        appearance: "outlined",
        class: "border border-secondary text-secondary-foreground bg-transparent hover:bg-secondary/10",
      },
      {
        variant: "brand",
        appearance: "outlined",
        class: "border border-brand-teal text-brand-teal bg-transparent hover:bg-brand-teal/10",
      },
      {
        variant: "brand2",
        appearance: "outlined",
        class: "border border-brand-yellow text-[#061d4a] bg-transparent hover:bg-brand-yellow/10",
      },
      {
        variant: "success",
        appearance: "outlined",
        class: "border border-status-complete text-status-complete bg-transparent hover:bg-status-complete/10",
      },
      {
        variant: "info",
        appearance: "outlined",
        class: "border border-blue-600 text-blue-600 bg-transparent hover:bg-blue-600/10",
      },
      {
        variant: "warning",
        appearance: "outlined",
        class: "border border-brand-amber text-brand-amber bg-transparent hover:bg-brand-amber/10",
      },
      {
        variant: "default",
        appearance: "text",
        class: "text-primary hover:bg-primary/10",
      },
      {
        variant: "destructive",
        appearance: "text",
        class: "text-destructive hover:bg-destructive/10",
      },
      {
        variant: "secondary",
        appearance: "text",
        class: "text-gray-500 hover:bg-secondary/10",
      },
      {
        variant: "brand",
        appearance: "text",
        class: "text-brand-teal hover:bg-brand-teal/10",
      },
      {
        variant: "brand2",
        appearance: "text",
        class: "text-brand-yellow hover:bg-brand-yellow/10",
      },
      {
        variant: "success",
        appearance: "text",
        class: "text-status-complete hover:bg-status-complete/10",
      },
      {
        variant: "info",
        appearance: "text",
        class: "text-blue-600 hover:bg-blue-600/10",
      },
      {
        variant: "warning",
        appearance: "text",
        class: "text-brand-amber hover:bg-brand-amber/10",
      },
    ],
    defaultVariants: {
      variant: "default",
      appearance: "solid",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, appearance, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, appearance, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
